import { useEffect, useState } from 'react';
import { StoreProductDetail, StoreVariantColor } from '../models';
import { useCartStore } from '../store/cartStore';
import { useToastStore } from '../store/toastStore';
import { extractUniqueColors, extractAvailableValues, resolveVariant } from '../utils/variantResolution';

const UNLIMITED_STOCK = Number.MAX_SAFE_INTEGER;

export function useVariantSelection(product: StoreProductDetail | null) {
  const addItem = useCartStore((s) => s.addItem);
  const showToast = useToastStore((s) => s.show);

  const [selectedColor, setSelectedColor] = useState<StoreVariantColor | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    setSelectedValue(null);
    setQuantity(1);

    if (!product) {
      setSelectedColor(null);
      return;
    }

    const colors = extractUniqueColors(product.variants);
    if (colors.length > 0) {
      const colorWithStock = colors.find((c) =>
        product.variants.some((v) => v.stockQuantity > 0 && v.color?.id === c.id)
      );
      setSelectedColor(colorWithStock ?? colors[0]);
    } else {
      setSelectedColor(null);
    }
  }, [product]);

  const hasVariants = (product?.variants.length ?? 0) > 0;
  const hasColors = product?.variants.some((v) => v.color !== null) ?? false;
  const uniqueColors = product && hasColors ? extractUniqueColors(product.variants) : [];
  const uniqueValues = product ? extractAvailableValues(product.variants, selectedColor) : [];
  const resolvedVariant = product ? resolveVariant(product.variants, hasColors, selectedColor, selectedValue) : null;
  const stockQuantity = hasVariants ? resolvedVariant?.stockQuantity ?? 0 : UNLIMITED_STOCK;
  const isOutOfStock = hasVariants && resolvedVariant !== null && resolvedVariant.stockQuantity === 0;
  const hasPrice = (product?.hasPrice ?? false) && product?.price !== null;
  const canAddToCart = !!product && (!hasVariants || resolvedVariant !== null) && !isOutOfStock && hasPrice;

  const selectColor = (color: StoreVariantColor) => {
    setSelectedColor((prev) => (prev?.id === color.id ? null : color));
    setSelectedValue(null);
  };

  const selectValue = (value: string) => {
    setSelectedValue((prev) => (prev === value ? null : value));
  };

  const increaseQuantity = () => {
    if (quantity < stockQuantity) setQuantity((q) => q + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity((q) => q - 1);
  };

  const addToCart = (): boolean => {
    if (!product || product.price === null) return false;
    if (hasVariants && !resolvedVariant) return false;

    const result = addItem({
      productId: product.id,
      variantId: resolvedVariant?.id ?? 0,
      productName: product.name,
      variantLabel: resolvedVariant?.label ?? '',
      variantSku: resolvedVariant?.sku,
      colorName: selectedColor?.name,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      imageUrl: product.images[0]?.imageUrl ?? '',
      quantity,
      stockQuantity: hasVariants ? resolvedVariant?.stockQuantity ?? 0 : UNLIMITED_STOCK,
    });

    if (result === 'max_stock') {
      showToast('Quantidade máxima em estoque atingida.', 'error');
      return false;
    }
    showToast('Produto adicionado ao carrinho!', 'success');
    return true;
  };

  return {
    hasVariants,
    hasColors,
    uniqueColors,
    uniqueValues,
    selectedColor,
    selectedValue,
    quantity,
    resolvedVariant,
    stockQuantity,
    isOutOfStock,
    hasPrice,
    canAddToCart,
    selectColor,
    selectValue,
    increaseQuantity,
    decreaseQuantity,
    addToCart,
  };
}
